import { useCallback, useRef, useState } from 'react';

import { MbscEventUpdateEvent, MbscEventUpdatedEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useUpdateAppointment from 'hooks/appointment/useUpdateAppointment';
import useUpdateClass from 'hooks/class/useUpdateClass';
import { useQueryClient } from 'react-query';
import { AppointmentRetrieve, AppointmentWrite, KiloClassRetrieve, KiloClassWrite } from 'types';
import { client } from 'utils/api-client';
import { isDateTimePast } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';
import { isNoCoachMove } from 'utils/scheduling.helpers';

import { EventCardInfo } from '../components/ModalDragNotificationConfirmation';
import { RecurrenceChoice } from '../components/ModalDragRecurrenceConfirmation';

type PendingDragData = {
  eventType: 'class' | 'appointment';
  eventId: string;
  newStartDate: string;
  newStartTime: string;
  newDuration: number;
  eventData: KiloClassRetrieve | AppointmentRetrieve;
  originalCoachId?: string;
  newCoachId?: string;
  newCoachName?: string;
};

export type DragPrefill = {
  startTime: string;
  originalCoachId?: string;
  newCoachId?: string;
  newCoachName?: string;
};

interface UseEventDragUpdateParams {
  handleEditClassEvent: (id: string, prefill?: DragPrefill) => void;
  handleEditAppointmentEvent: (id: string, prefill?: DragPrefill) => void;
  allowDateChange?: boolean;
  resolveCoachName?: (id: string) => string | undefined;
}

interface UseEventDragUpdateReturn {
  handleEventUpdate: (args: MbscEventUpdateEvent) => void;
  handleEventUpdated: (args: MbscEventUpdatedEvent) => void;
  recurrenceModal: {
    opened: boolean;
    eventName: string;
    onCancel: () => void;
    onContinue: (choice: RecurrenceChoice) => void;
  };
  notificationModal: {
    opened: boolean;
    oldEvent: EventCardInfo | null;
    newEvent: EventCardInfo | null;
    onCancel: () => void;
    onDontSend: () => void;
    onSend: () => void;
  };
}

const getDurationMinutes = (start: Date, end: Date): number => dayjs(end).diff(dayjs(start), 'minute');

const formatTime = (date: Date): string => dayjs(date).format('HH:mm:ss');

const buildClassPayload = (
  data: KiloClassRetrieve,
  newStartDate: string,
  newStartTime: string,
  newDuration: number,
  editThisEventOnly: boolean,
  sendNotification: boolean,
  originalCoachId?: string,
  newCoachId?: string,
): KiloClassWrite => {
  const newCoachAlreadyOnEvent =
    newCoachId != null && data.kiloClassCoachKiloClassCoachRoles.some(r => r.coach.staffId === newCoachId);

  return {
    kiloClassId: data.kiloClassId,
    startDate: newStartDate,
    startTime: newStartTime,
    duration: newDuration,
    endDate: data.endDate,
    rrule: data.rrule,
    capacity: data.capacity,
    kiloClassType: data.kiloClassType.kiloClassTypeId,
    location: data.location?.locationId ?? null,
    rootKiloClass: data.rootKiloClass?.kiloClassId ?? null,
    waitlistLimit: data.waitlistLimit,
    reservationAndCancellationSettings: data.reservationAndCancellationSettings,
    kiloClassCoachKiloClassCoachRoles: data.kiloClassCoachKiloClassCoachRoles
      .filter(r => !(r.coach.staffId === originalCoachId && newCoachAlreadyOnEvent))
      .map(r => ({
        coach: r.coach.staffId === originalCoachId && newCoachId ? newCoachId : r.coach.staffId,
        kiloClassCoachRole: r.kiloClassCoachRole.kiloClassCoachRoleId,
      })),
    editThisEventOnly,
    sendNotification,
  };
};

const buildAppointmentPayload = (
  data: AppointmentRetrieve,
  newStartDate: string,
  newStartTime: string,
  newDuration: number,
  editThisEventOnly: boolean,
  sendNotification: boolean,
  originalCoachId?: string,
  newCoachId?: string,
): AppointmentWrite => {
  const newCoachAlreadyOnEvent =
    newCoachId != null &&
    data.appointmentCoachAppointmentCoachRoles.some(r => r.coach.staffId === newCoachId);

  return {
    appointmentId: data.appointmentId,
    startDate: newStartDate,
    startTime: newStartTime,
    duration: newDuration,
    endDate: data.endDate,
    rrule: data.rrule,
    appointmentType: data.appointmentType.appointmentTypeId,
    location: data.location?.locationId ?? null,
    rootAppointment: data.rootAppointment?.appointmentId ?? null,
    cancellationSettings: data.cancellationSettings,
    athletes: [],
    appointmentCoachAppointmentCoachRoles: data.appointmentCoachAppointmentCoachRoles
      .filter(r => !(r.coach.staffId === originalCoachId && newCoachAlreadyOnEvent))
      .map(r => ({
        coach: r.coach.staffId === originalCoachId && newCoachId ? newCoachId : r.coach.staffId,
        appointmentCoachRole: r.appointmentCoachRole.appointmentCoachRoleId,
      })),
    editThisEventOnly,
    sendNotification,
  };
};

const getOriginalCoachName = (drag: PendingDragData): string => {
  const { eventType, eventData, originalCoachId } = drag;
  if (eventType === 'class') {
    const roles = (eventData as KiloClassRetrieve).kiloClassCoachKiloClassCoachRoles;
    if (originalCoachId) {
      const role = roles.find(r => r.coach.staffId === originalCoachId);
      if (role) return role.coach.fullName;
    }
    return roles[0]?.coach.fullName ?? '';
  }
  const roles = (eventData as AppointmentRetrieve).appointmentCoachAppointmentCoachRoles;
  if (originalCoachId) {
    const role = roles.find(r => r.coach.staffId === originalCoachId);
    if (role) return role.coach.fullName;
  }
  return roles[0]?.coach.fullName ?? '';
};

const getEventName = (drag: PendingDragData): string =>
  drag.eventType === 'class'
    ? (drag.eventData as KiloClassRetrieve).kiloClassType.name
    : (drag.eventData as AppointmentRetrieve).appointmentType.name;

const buildEventCardInfo = (drag: PendingDragData, useNewTime: boolean): EventCardInfo => {
  const { newStartDate, newStartTime, newDuration, newCoachName, eventData } = drag;
  const source = eventData as { startDate: string; startTime: string; duration: number };
  const originalCoachName = getOriginalCoachName(drag);
  const coachName = useNewTime && newCoachName ? newCoachName : originalCoachName;

  return {
    eventName: getEventName(drag),
    coachName,
    date: useNewTime ? newStartDate : source.startDate,
    startTime: useNewTime ? newStartTime : source.startTime,
    duration: useNewTime ? newDuration : source.duration,
  };
};

export default function useEventDragUpdate({
  handleEditClassEvent,
  handleEditAppointmentEvent,
  allowDateChange = true,
  resolveCoachName,
}: UseEventDragUpdateParams): UseEventDragUpdateReturn {
  const updateClass = useUpdateClass();
  const updateAppointment = useUpdateAppointment();
  const queryClient = useQueryClient();
  const { errorNotification } = notificationHandler();

  const [pendingDrag, setPendingDrag] = useState<PendingDragData | null>(null);
  const [editThisEventOnly, setEditThisEventOnly] = useState(false);
  const [recurrenceModalOpened, setRecurrenceModalOpened] = useState(false);
  const [notificationModalOpened, setNotificationModalOpened] = useState(false);

  const invalidateEvent = (eventType: 'class' | 'appointment'): void => {
    if (eventType === 'class') {
      queryClient.invalidateQueries(['classes']);
    } else {
      queryClient.invalidateQueries(['appointments']);
    }
  };

  const clearPendingDrag = (): void => {
    setPendingDrag(null);
    setEditThisEventOnly(false);
  };

  const fetchAndSetPendingDrag = async (
    eventType: 'class' | 'appointment',
    eventId: string,
    newStartDate: string,
    newStartTime: string,
    newDuration: number,
    originalCoachId?: string,
    newCoachId?: string,
  ): Promise<void> => {
    const url = eventType === 'class' ? `kilo-classes/${eventId}/` : `appointments/${eventId}/`;
    const { data } = await client.get<KiloClassRetrieve | AppointmentRetrieve>(url);

    if (!allowDateChange && newStartDate !== data.startDate) {
      errorNotification('Date changes are not allowed in the day view.');
      invalidateEvent(eventType);
      return;
    }

    const coachChanged = newCoachId != null && newCoachId !== originalCoachId;
    const drag: PendingDragData = {
      eventType,
      eventId,
      newStartDate,
      newStartTime,
      newDuration,
      eventData: data,
      originalCoachId,
      newCoachId: coachChanged ? newCoachId : undefined,
      newCoachName: coachChanged ? resolveCoachName?.(newCoachId) : undefined,
    };
    setPendingDrag(drag);
    if (data.rrule != null) {
      setRecurrenceModalOpened(true);
    } else {
      setNotificationModalOpened(true);
    }
  };

  const handleEventUpdatedImpl = async (args: MbscEventUpdatedEvent): Promise<void> => {
    const { event } = args;
    const eventType = event.eventType as 'class' | 'appointment';
    const eventRecord = event as Record<string, unknown>;
    const eventId = (eventRecord.originalId ?? event.id)?.toString();
    if (!eventId) return;
    const originalCoachId = eventRecord.coachId as string | undefined;
    const newCoachId = event.resource as string | undefined;

    const newStart = new Date(event.start as string);
    const newEnd = new Date(event.end as string);
    const newStartTime = formatTime(newStart);
    const newDuration = getDurationMinutes(newStart, newEnd);
    const newStartDate = dayjs(newStart).format('YYYY-MM-DD');

    try {
      await fetchAndSetPendingDrag(
        eventType,
        eventId,
        newStartDate,
        newStartTime,
        newDuration,
        originalCoachId,
        newCoachId,
      );
    } catch (error: unknown) {
      errorNotification(
        `Failed to load ${eventType} data`,
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message,
      );
      invalidateEvent(eventType);
    }
  };

  const handleEventUpdate = useCallback((args: MbscEventUpdateEvent) => {
    const event = args.event as Record<string, unknown>;
    if (event.nonDraggable) {
      notificationHandler().errorNotification(
        'This event cannot be rescheduled.',
        'Events that are in the past, have athletes checked-in, late-cancelled, or no-show cannot be dragged.',
      );
      return false;
    }
    if (isNoCoachMove(args.oldEvent?.resource, args.event.resource)) {
      notificationHandler().errorNotification(
        'Cannot move an event to or from No Coach.',
        'Assign a coach from the event details.',
      );
      return false;
    }
    const newStart = dayjs(args.event.start as string);
    const newEnd = dayjs(args.event.end as string);
    const newStartDate = newStart.format('YYYY-MM-DD');
    const newStartTime = newStart.format('HH:mm:ss');
    if (isDateTimePast(newStartDate, newStartTime)) {
      notificationHandler().errorNotification(
        'Cannot move an event to a date in the past.',
        'Drag the event to a future date or time to reschedule it.',
      );
      return false;
    }
    if (newEnd.format('YYYY-MM-DD') !== newStartDate) {
      notificationHandler().errorNotification(
        'Events cannot span multiple days.',
        'Resize the event to end on the same day.',
      );
      return false;
    }
  }, []);

  const handleEventUpdatedRef = useRef(handleEventUpdatedImpl);
  handleEventUpdatedRef.current = handleEventUpdatedImpl;

  const handleEventUpdated = useCallback((args: MbscEventUpdatedEvent): void => {
    handleEventUpdatedRef.current(args);
  }, []);

  const handleRecurrenceCancel = (): void => {
    setRecurrenceModalOpened(false);
    if (pendingDrag) invalidateEvent(pendingDrag.eventType);
    clearPendingDrag();
  };

  const handleRecurrenceContinue = (choice: RecurrenceChoice): void => {
    setRecurrenceModalOpened(false);

    if (choice === 'all-future-events') {
      if (pendingDrag) {
        invalidateEvent(pendingDrag.eventType);
        const prefill: DragPrefill = {
          startTime: pendingDrag.newStartTime,
          originalCoachId: pendingDrag.originalCoachId,
          newCoachId: pendingDrag.newCoachId,
          newCoachName: pendingDrag.newCoachName,
        };
        if (pendingDrag.eventType === 'class') {
          handleEditClassEvent(pendingDrag.eventId, prefill);
        } else {
          handleEditAppointmentEvent(pendingDrag.eventId, prefill);
        }
      }
      clearPendingDrag();
      return;
    }

    setEditThisEventOnly(true);
    setNotificationModalOpened(true);
  };

  const handleNotificationCancel = (): void => {
    setNotificationModalOpened(false);
    if (pendingDrag) invalidateEvent(pendingDrag.eventType);
    clearPendingDrag();
  };

  const submitUpdate = async (sendNotification: boolean): Promise<void> => {
    if (!pendingDrag) return;

    const { eventType, newStartDate, newStartTime, newDuration, eventData, originalCoachId, newCoachId } =
      pendingDrag;
    setNotificationModalOpened(false);
    clearPendingDrag();

    try {
      if (eventType === 'class') {
        await updateClass.mutateAsync(
          buildClassPayload(
            eventData as KiloClassRetrieve,
            newStartDate,
            newStartTime,
            newDuration,
            editThisEventOnly,
            sendNotification,
            originalCoachId,
            newCoachId,
          ),
        );
      } else {
        await updateAppointment.mutateAsync(
          buildAppointmentPayload(
            eventData as AppointmentRetrieve,
            newStartDate,
            newStartTime,
            newDuration,
            editThisEventOnly,
            sendNotification,
            originalCoachId,
            newCoachId,
          ),
        );
      }
    } catch (error: unknown) {
      errorNotification(
        `Failed to update ${eventType}`,
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message,
      );
      invalidateEvent(eventType);
    }
  };

  return {
    handleEventUpdate,
    handleEventUpdated,
    recurrenceModal: {
      opened: recurrenceModalOpened,
      eventName: pendingDrag ? getEventName(pendingDrag) : '',
      onCancel: handleRecurrenceCancel,
      onContinue: handleRecurrenceContinue,
    },
    notificationModal: {
      opened: notificationModalOpened,
      oldEvent: pendingDrag ? buildEventCardInfo(pendingDrag, false) : null,
      newEvent: pendingDrag ? buildEventCardInfo(pendingDrag, true) : null,
      onCancel: handleNotificationCancel,
      onDontSend: () => submitUpdate(false),
      onSend: () => submitUpdate(true),
    },
  };
}
